import hotkeysJs, { KeyHandler } from 'hotkeys-js';
import { Key } from 'ts-key-enum';
import { ACTION_TYPE_ID } from '@contract';
import { SPORT, Sport } from '@/service/constants';
import {
  buildPlayerNumber,
  cancelTargetActionMode,
  reset,
  resetToPitchMode,
  setMode,
} from '@/stores/ActionStore/ActionStore';
import {
  commitAction,
  commitGoalkeeperSave,
  commitShot,
  toggleActionOutcome,
  tryToggleHeaded,
} from '@/stores/ActionStore/commitActions';
import { MODE, Mode } from '@/stores/ActionStore/constants';
import { toggleDrawer } from '@/stores/FiltersStore/FiltersStore';
import { onNumericKeyInput, onSingleModifierKey } from './helpers';

export const HOTKEYS_VARIANT = {
  COLLECTOR: 'collector',
  QA: 'qa',
} as const;

export type HotkeysVariant = ConstType<typeof HOTKEYS_VARIANT>;
type HotkeyOptions = Parameters<typeof hotkeysJs>[1] | undefined;

export type Hotkey = [string, KeyHandler] | [string, KeyHandler, HotkeyOptions];
export type HotkeyConfig<T extends string = string> = Record<T, Hotkey[]>;

const collectorHotkeys: HotkeyConfig<Mode> = {
  [MODE.PITCH]: [
    [Key.Escape, reset],
    [Key.Backspace, toggleActionOutcome],
    [Key.Enter, setMode.bind(null, MODE.PASS)],
    ['space', setMode.bind(null, MODE.SHOT)],
    ['g', setMode.bind(null, MODE.GOAL)],
    ['up', setMode.bind(null, MODE.LAUNCH)],
    ['down', commitAction.bind(null, ACTION_TYPE_ID.Tackle)],
    ['left', commitAction.bind(null, ACTION_TYPE_ID.BallRecovery)],
    ['right', commitAction.bind(null, ACTION_TYPE_ID.Dispossessed)],
    [Key.Home, commitAction.bind(null, ACTION_TYPE_ID.Clearance)],
    [Key.PageUp, commitAction.bind(null, ACTION_TYPE_ID.Aerial)],
    [Key.PageDown, commitAction.bind(null, ACTION_TYPE_ID.Interception)],
    ['num_subtract', setMode.bind(null, MODE.CROSS)],
    ['s', setMode.bind(null, MODE.GOALKEEPER_SAVE)],
    ['c', commitAction.bind(null, ACTION_TYPE_ID.CornerAwarded)],
    [Key.End, commitAction.bind(null, ACTION_TYPE_ID.BallTouch)],
    [Key.Insert, commitAction.bind(null, ACTION_TYPE_ID.ThrowInAwarded)],
    [Key.Delete, setMode.bind(null, MODE.THROW_IN)],
    ['t', commitAction.bind(null, ACTION_TYPE_ID.BadTouch)],
    ['num_divide', commitAction.bind(null, ACTION_TYPE_ID.PlayerBeaten)],
    ['num_multiply', commitAction.bind(null, ACTION_TYPE_ID.TakeOn)],
    [Key.F1, commitAction.bind(null, ACTION_TYPE_ID.RefereeDropBall)],
    [
      '*',
      onSingleModifierKey(Key.Shift, setMode.bind(null, MODE.FREE_KICK)),
      { keyup: true },
    ],
    ['*', onSingleModifierKey(Key.Control, tryToggleHeaded), { keyup: true }],
    ['*', onNumericKeyInput(buildPlayerNumber)],
  ],
  [MODE.PASS]: [
    ['p', cancelTargetActionMode],
    [Key.Escape, cancelTargetActionMode],
  ],
  [MODE.CROSS]: [
    ['p', cancelTargetActionMode],
    [Key.Escape, cancelTargetActionMode],
  ],
  [MODE.LAUNCH]: [
    ['p', cancelTargetActionMode],
    [Key.Escape, cancelTargetActionMode],
  ],
  [MODE.GOALKEEPER_THROW]: [
    ['p', cancelTargetActionMode],
    [Key.Escape, cancelTargetActionMode],
  ],
  [MODE.GOALKEEPER_KICK_FROM_HANDS]: [
    ['p', cancelTargetActionMode],
    [Key.Escape, cancelTargetActionMode],
  ],
  [MODE.SHOT]: [['space', commitShot]],
  [MODE.GOAL]: [],
  [MODE.THROW_IN]: [[Key.Escape, resetToPitchMode]],
  [MODE.YELLOW_CARD]: [],
  [MODE.RED_CARD]: [],
  [MODE.VAR_UNDERWAY]: [],
  [MODE.VAR_RESULT]: [],
  [MODE.SUBSTITUTION]: [],
  [MODE.OFFSIDE]: [],
  [MODE.FREE_KICK]: [],
  [MODE.PLAY_STOPPED]: [],
  [MODE.GOALKEEPER_PENALTY_FACED]: [],
  [MODE.GOALKEEPER_SAVE]: [['s', commitGoalkeeperSave]],
  [MODE.ADDITIONAL_TIME]: [],
  [MODE.ERROR]: [],
};

type qaHotkeys = Pick<HotkeyConfig<Mode>, 'pitch'>;

const qaHotkeys: qaHotkeys = {
  [MODE.PITCH]: [['f', toggleDrawer]],
};

const HOTKEY_CONFIG = {
  [SPORT.SOCCER]: {
    [HOTKEYS_VARIANT.COLLECTOR]: collectorHotkeys,
    [HOTKEYS_VARIANT.QA]: qaHotkeys,
  },
} as const;

export function getHotkeysConfig(sport: Sport, hotkeysVariant: HotkeysVariant) {
  return HOTKEY_CONFIG[sport][hotkeysVariant];
}

export type HotkeyRegisterEntry = {
  key: string;
  scope: string;
  callback: KeyHandler;
  options?: HotkeyOptions;
};

export function registerHotkey({
  key,
  scope,
  options,
  callback,
}: HotkeyRegisterEntry) {
  hotkeysJs(key, { scope, ...options }, callback);
}
export function registerHotkeys(hotkeysConfig: HotkeyConfig) {
  Object.entries(hotkeysConfig).forEach(([mode, hotkeys]) => {
    hotkeys.forEach(([key, callback, options]) => {
      registerHotkey({ key, scope: mode, options, callback });
    });
  });
}

export function unregisterHotkey({
  key,
  scope,
  callback,
}: HotkeyRegisterEntry) {
  hotkeysJs.unbind(key, scope, callback);
}
export function unregisterHotkeys(hotkeysConfig: HotkeyConfig) {
  Object.entries(hotkeysConfig).forEach(([mode, hotkeys]) => {
    hotkeys.forEach(([key, callback]) => {
      unregisterHotkey({ key, scope: mode, callback });
    });
  });
}
